/**
 * Trip Layer Analysis Functions
 * 
 * Analyzes trip layers to extract demand patterns and calculate
 * vehicle requirements for fleet generation.
 */

import { Layer, Line, Shape } from '../../../../context/context';
import { TripAnalysisResult } from './types';
import { parseTime, formatTime, parseTimeForgiving } from './timeUtils';

/**
 * Check if a line/shape represents a trip
 * Trips have ride_id or both originPointId and destPointId
 */
export function isCanonicalTripLine(line: Line): boolean {
  return !!(
    line.ride_id ||
    (line.type === 'line' && 
     'originPointId' in line && 
     'destPointId' in line &&
     (line as any).originPointId && 
     (line as any).destPointId)
  );
}

export function isCanonicalTripLineShape(shape: Shape): shape is Line {
  return shape.type === 'line' && isCanonicalTripLine(shape as Line);
}

export function getCanonicalTripLines(layer: Layer): Line[] {
  return layer.shapes.filter((shape): shape is Line => isCanonicalTripLineShape(shape));
}

export type WizardTripLayerFailureReason = 'no_trip_layer' | 'no_valid_time_data';

export function selectWizardTripLayer(
  layers: Layer[],
  options: {
    authoritativeRideLayerId?: string | null;
    recentRideLayerId?: string | null;
    requireTimeData?: boolean;
  }
): {
  layer: Layer | null;
  failureReason?: WizardTripLayerFailureReason;
} {
  const requireTimeData = options.requireTimeData ?? true;
  const orderedIds: string[] = [];
  const pushUnique = (id?: string | null) => {
    if (!id) return;
    if (!orderedIds.includes(id)) orderedIds.push(id);
  };

  pushUnique(options.authoritativeRideLayerId ?? null);
  pushUnique(options.recentRideLayerId ?? null);
  [...layers]
    .reverse()
    .forEach((layer) => pushUnique(String(layer.layerId)));

  let foundTripLayerWithoutTime = false;

  for (const layerId of orderedIds) {
    const layer = layers.find((entry) => String(entry.layerId) === layerId);
    if (!layer) continue;

    const trips = getCanonicalTripLines(layer);
    if (trips.length === 0) continue;

    if (!requireTimeData) {
      return { layer };
    }

    const tripsWithTime = trips.filter((trip) => extractRequestTime(trip) !== null).length;
    if (tripsWithTime > 0) {
      return { layer };
    }

    foundTripLayerWithoutTime = true;
  }

  if (requireTimeData && foundTripLayerWithoutTime) {
    return { layer: null, failureReason: 'no_valid_time_data' };
  }

  return { layer: null, failureReason: 'no_trip_layer' };
}

/**
 * Extract trip request time from a line shape
 * Handles various time formats: full datetime, time-only, or legacy 'time' field
 */
function extractRequestTime(trip: Line): Date | null {
  // Check request_time field (preferred)
  if ((trip as any).request_time) {
    const requestTime = (trip as any).request_time;
    try {
      const date = new Date(requestTime);
      if (!isNaN(date.getTime())) {
        return date;
      }
    } catch {
      // Fall through to other attempts
    }
  }

  // Check legacy 'time' field
  if (trip.time) {
    try {
      const timeValue = typeof trip.time === 'string' ? trip.time : String(trip.time);
      const date = new Date(timeValue);
      if (!isNaN(date.getTime())) {
        return date;
      }
    } catch {
      // Fall through
    }
  }

  return null;
}

/**
 * Extract hour (0-23) from a trip's request time
 */
function extractHourFromTrip(trip: Line): number | null {
  const date = extractRequestTime(trip);
  if (!date) {
    return null;
  }
  return date.getHours();
}

/**
 * Get all trip layers from a list of layers
 * A trip layer contains lines with ride_id or origin/destination point IDs
 * 
 * @param layers - All available layers
 * @returns Array of layers that contain trips
 */
export function getTripLayersFromContext(layers: Layer[]): Layer[] {
  return layers.filter(layer => {
    const trips = getCanonicalTripLines(layer);
    return trips.length > 0;
  });
}

/**
 * Build hourly demand distribution from trips
 * Counts trips per hour (0-23) based on request times
 * 
 * @param trips - Array of trip lines
 * @returns Object mapping hour (0-23) to trip count
 */
export function buildHourlyDemandDistribution(trips: Line[]): Record<number, number> {
  const distribution: Record<number, number> = {};
  
  // Initialize all hours to 0
  for (let hour = 0; hour < 24; hour++) {
    distribution[hour] = 0;
  }

  // Count trips per hour
  trips.forEach(trip => {
    const hour = extractHourFromTrip(trip);
    if (hour !== null && hour >= 0 && hour < 24) {
      distribution[hour]++;
    }
  });

  return distribution;
}

/**
 * Calculate vehicle requirements based on demand distribution
 * Uses peak demand and trips per vehicle per hour ratio
 * 
 * @param demandDist - Hourly demand distribution (hour -> trip count)
 * @param tripsPerVehiclePerHour - Expected trips a vehicle can handle per hour (default: 5)
 * @returns Number of vehicles needed
 */
export function calculateVehicleRequirements(
  demandDist: Record<number, number>,
  tripsPerVehiclePerHour: number = 5
): number {
  // Find peak demand (maximum trips in any hour)
  const peakDemand = Math.max(...Object.values(demandDist));
  
  if (peakDemand === 0) {
    return 1; // Minimum 1 vehicle
  }

  // Calculate vehicles needed based on peak demand
  const vehiclesNeeded = Math.ceil(peakDemand / tripsPerVehiclePerHour);
  
  return Math.max(1, vehiclesNeeded);
}

/**
 * Extract service hours from trips
 * Finds earliest and latest trip times to suggest service window
 * 
 * @param trips - Array of trip lines
 * @returns Object with start and end time in HH:MM format
 */
export function extractServiceHours(trips: Line[]): { start: string; end: string } {
  const times: Date[] = [];

  trips.forEach(trip => {
    const date = extractRequestTime(trip);
    if (date) {
      times.push(date);
    }
  });

  if (times.length === 0) {
    // Default service hours if no times found
    return { start: '06:00', end: '22:00' };
  }

  // Find earliest and latest times
  const earliestTime = new Date(Math.min(...times.map(t => t.getTime())));
  const latestTime = new Date(Math.max(...times.map(t => t.getTime())));

  // Extract hours and minutes
  const startHour = earliestTime.getHours();
  const startMinute = earliestTime.getMinutes();
  const endHour = latestTime.getHours();
  const endMinute = latestTime.getMinutes();

  // Format as HH:MM
  const startTime = `${startHour.toString().padStart(2, '0')}:${startMinute.toString().padStart(2, '0')}`;
  const endTime = `${endHour.toString().padStart(2, '0')}:${endMinute.toString().padStart(2, '0')}`;

  return { start: startTime, end: endTime };
}

/**
 * Detect peak periods automatically from hourly demand
 * A peak period is a continuous range of hours with demand above threshold
 * 
 * @param hourlyDemand - Hourly demand distribution
 * @param threshold - Percentage of peak demand to consider as peak (default: 0.7 = 70%)
 * @param minDuration - Minimum duration in hours for a peak period (default: 1)
 * @returns Array of detected peak periods with start/end times
 */
export function detectPeakPeriods(
  hourlyDemand: Record<number, number>,
  threshold: number = 0.7,
  minDuration: number = 1
): Array<{ start: string; end: string; demand: number }> {
  const peakDemand = Math.max(...Object.values(hourlyDemand));
  const peakThreshold = peakDemand * threshold;
  
  const peaks: Array<{ start: string; end: string; demand: number }> = [];
  let currentPeakStart: number | null = null;
  let currentPeakDemand = 0;

  for (let hour = 0; hour < 24; hour++) {
    const demand = hourlyDemand[hour] || 0;

    if (demand >= peakThreshold) {
      // Start or continue a peak period
      if (currentPeakStart === null) {
        currentPeakStart = hour;
        currentPeakDemand = demand;
      } else {
        currentPeakDemand = Math.max(currentPeakDemand, demand);
      }
    } else {
      // End a peak period if one was in progress
      if (currentPeakStart !== null) {
        const duration = hour - currentPeakStart;
        if (duration >= minDuration) {
          peaks.push({
            start: `${currentPeakStart.toString().padStart(2, '0')}:00`,
            end: `${hour.toString().padStart(2, '0')}:00`,
            demand: currentPeakDemand,
          });
        }
        currentPeakStart = null;
        currentPeakDemand = 0;
      }
    }
  }

  // Handle peak that extends to end of day
  if (currentPeakStart !== null) {
    const duration = 24 - currentPeakStart;
    if (duration >= minDuration) {
      peaks.push({
        start: `${currentPeakStart.toString().padStart(2, '0')}:00`,
        end: '23:59',
        demand: currentPeakDemand,
      });
    }
  }

  return peaks;
}

/**
 * Calculate hourly vehicle requirements based on demand
 * Returns how many vehicles are needed for each hour
 * 
 * @param hourlyDemand - Hourly demand distribution
 * @param tripsPerVehiclePerHour - Expected trips per vehicle per hour
 * @returns Object mapping hour to required vehicle count
 */
export function calculateHourlyVehicleRequirements(
  hourlyDemand: Record<number, number>,
  tripsPerVehiclePerHour: number = 5
): Record<number, number> {
  const hourlyVehicles: Record<number, number> = {};

  for (let hour = 0; hour < 24; hour++) {
    const demand = hourlyDemand[hour] || 0;
    const vehiclesNeeded = demand > 0 ? Math.ceil(demand / tripsPerVehiclePerHour) : 0;
    hourlyVehicles[hour] = vehiclesNeeded;
  }

  return hourlyVehicles;
}

/**
 * Analyze a trip layer to extract comprehensive demand information
 * Main function that combines all analysis functions
 * 
 * @param layer - Layer containing trip data
 * @param tripsPerVehiclePerHour - Expected trips per vehicle per hour (default: 5)
 * @returns Complete analysis result with demand patterns and vehicle calculations
 */
export function analyzeTripsLayer(
  layer: Layer,
  tripsPerVehiclePerHour: number = 5
): TripAnalysisResult {
  // Filter for trip lines only
  const trips = getCanonicalTripLines(layer);

  // Build hourly demand distribution
  const hourlyDemand = buildHourlyDemandDistribution(trips);

  // Find peak hour and demand
  let peakHour = 0;
  let peakDemand = 0;
  Object.entries(hourlyDemand).forEach(([hour, count]) => {
    if (count > peakDemand) {
      peakDemand = count;
      peakHour = parseInt(hour, 10);
    }
  });

  // Calculate vehicle requirements (max needed)
  const calculatedVehicles = calculateVehicleRequirements(hourlyDemand, tripsPerVehiclePerHour);

  // Calculate hourly vehicle requirements
  const hourlyVehicles = calculateHourlyVehicleRequirements(hourlyDemand, tripsPerVehiclePerHour);

  // Detect peak periods automatically
  const detectedPeaks = detectPeakPeriods(hourlyDemand, 0.7, 1);

  // Extract service hours
  const serviceHours = extractServiceHours(trips);

  return {
    totalTrips: trips.length,
    hourlyDemand,
    peakHour,
    peakDemand,
    suggestedServiceStart: serviceHours.start,
    suggestedServiceEnd: serviceHours.end,
    calculatedVehicles,
    hourlyVehicleRequirements: hourlyVehicles,
    detectedPeakPeriods: detectedPeaks,
  };
}

/**
 * Validate that a layer has sufficient trip data for analysis
 * 
 * @param layer - Layer to validate
 * @returns Object with isValid flag and error message if invalid
 */
export function validateTripLayer(layer: Layer): { isValid: boolean; error?: string } {
  const trips = getCanonicalTripLines(layer);

  if (trips.length === 0) {
    return {
      isValid: false,
      error: 'The selected layer contains no valid trips. Please select a different layer or use Manual Input mode.',
    };
  }

  // Check if trips have time data
  const tripsWithTime = trips.filter(trip => extractRequestTime(trip) !== null);
  if (tripsWithTime.length === 0) {
    return {
      isValid: false,
      error: 'Trips in this layer do not have request times. Time data is required for demand analysis.',
    };
  }

  // Warn if less than 50% of trips have time data
  if (tripsWithTime.length < trips.length * 0.5) {
    return {
      isValid: true,
      error: `Only ${tripsWithTime.length} of ${trips.length} trips have time data. Analysis may be incomplete.`,
    };
  }

  return { isValid: true };
}

/**
 * Get summary statistics for a trip layer
 * Useful for displaying layer information before analysis
 * 
 * @param layer - Layer to summarize
 * @returns Summary statistics
 */
export function getTripLayerSummary(layer: Layer): {
  totalShapes: number;
  tripCount: number;
  tripsWithTime: number;
  hasRequestTypes: boolean;
  hasPassengerData: boolean;
} {
  const trips = getCanonicalTripLines(layer);

  const tripsWithTime = trips.filter(trip => extractRequestTime(trip) !== null).length;
  const hasRequestTypes = trips.some(trip => trip.request_type);
  const hasPassengerData = trips.some(trip => 
    trip.standard_seat !== undefined || 
    trip.wheelchair !== undefined || 
    trip.numPassengers !== undefined
  );

  return {
    totalShapes: layer.shapes.length,
    tripCount: trips.length,
    tripsWithTime,
    hasRequestTypes,
    hasPassengerData,
  };
}

